import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useData, type SensorType } from "@/contexts/data-context"
import { Thermometer, Droplets, Gauge, Zap, Sun, Wind, Activity, Users, BatteryMedium } from "lucide-react"
import { Progress } from "@/components/ui/progress"

export default function SensorOverview() {
  const { sensors, getSensorsByType, getAverageByType } = useData()

  // Group sensors by type
  const sensorTypes = Array.from(new Set(sensors.map((s) => s.type)))

  // Calculate offline sensors
  const offlineSensors = sensors.filter((s) => !s.isOnline).length
  const totalSensors = sensors.length
  const offlinePercentage = totalSensors > 0 ? (offlineSensors / totalSensors) * 100 : 0

  // Calculate low battery sensors
  const lowBatterySensors = sensors.filter((s) => s.batteryLevel !== undefined && s.batteryLevel < 20).length
  const sensorsWithBattery = sensors.filter((s) => s.batteryLevel !== undefined).length
  const lowBatteryPercentage = sensorsWithBattery > 0 ? (lowBatterySensors / sensorsWithBattery) * 100 : 0

  return (
    <div className="space-y-4">
      {/* System Status */}
      <div className="grid grid-cols-1 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">System Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span>Online Sensors:</span>
                <span className="font-medium">
                  {totalSensors - offlineSensors} / {totalSensors}
                </span>
              </div>
              <Progress value={100 - offlinePercentage} className="h-2" />

              {sensorsWithBattery > 0 && (
                <>
                  <div className="flex justify-between text-sm mt-3">
                    <span className="flex items-center gap-1">
                      <BatteryMedium className="w-4 h-4" /> Battery Status:
                    </span>
                    <span className="font-medium">
                      {sensorsWithBattery - lowBatterySensors} / {sensorsWithBattery} OK
                    </span>
                  </div>
                  <Progress
                    value={100 - lowBatteryPercentage}
                    className={`h-2 ${lowBatteryPercentage > 30 ? "bg-destructive/20" : ""}`}
                  />
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col items-center justify-center p-2 bg-destructive/10 rounded-md min-h-[60px]">
                <span className="text-xl font-bold text-destructive">
                  {sensors.filter((s) => s.status === "critical").length}
                </span>
                <span className="text-xs text-muted-foreground text-center">Critical</span>
              </div>
              <div className="flex flex-col items-center justify-center p-2 bg-warning/10 rounded-md min-h-[60px]">
                <span className="text-xl font-bold text-warning">
                  {sensors.filter((s) => s.status === "warning").length}
                </span>
                <span className="text-xs text-muted-foreground text-center">Warning</span>
              </div>
              <div className="flex flex-col items-center justify-center p-2 bg-success/10 rounded-md min-h-[60px]">
                <span className="text-xl font-bold text-success">
                  {sensors.filter((s) => s.status === "normal").length}
                </span>
                <span className="text-xs text-muted-foreground text-center">Normal</span>
              </div>
              <div className="flex flex-col items-center justify-center p-2 bg-muted/20 rounded-md min-h-[60px]">
                <span className="text-xl font-bold">{sensors.length}</span>
                <span className="text-xs text-muted-foreground text-center">Total</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sensor Type Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sensorTypes.map((type) => {
          const typeSensors = getSensorsByType(type)
          const avgValue = getAverageByType(type)

          if (typeSensors.length === 0 || avgValue === null) return null

          return (
            <SensorTypeCard
              key={type}
              type={type}
              count={typeSensors.length}
              avgValue={avgValue}
              unit={typeSensors[0].unit}
              criticalCount={typeSensors.filter((s) => s.status === "critical").length}
              warningCount={typeSensors.filter((s) => s.status === "warning").length}
            />
          )
        })}
      </div>
    </div>
  )
}

interface SensorTypeCardProps {
  type: SensorType
  count: number
  avgValue: number
  unit: string
  criticalCount: number
  warningCount: number
}

function SensorTypeCard({ type, count, avgValue, unit, criticalCount, warningCount }: SensorTypeCardProps) {
  const getTypeIcon = () => {
    switch (type) {
      case "temperature":
        return <Thermometer className="w-4 h-4" />
      case "humidity":
        return <Droplets className="w-4 h-4" />
      case "pressure":
        return <Gauge className="w-4 h-4" />
      case "light":
        return <Sun className="w-4 h-4" />
      case "electrical":
      case "power":
      case "voltage":
      case "current":
        return <Zap className="w-4 h-4" />
      case "co2":
      case "pm25":
      case "pm10":
      case "voc":
        return <Wind className="w-4 h-4" />
      case "noise":
        return <Activity className="w-4 h-4" />
      case "motion":
        return <Activity className="w-4 h-4" />
      case "occupancy":
        return <Users className="w-4 h-4" />
      case "water":
        return <Droplets className="w-4 h-4" />
      default:
        return <Activity className="w-4 h-4" />
    }
  }

  const getTypeName = () => {
    switch (type) {
      case "temperature":
        return "Temperature"
      case "humidity":
        return "Humidity"
      case "pressure":
        return "Pressure"
      case "light":
        return "Light"
      case "power":
        return "Power"
      case "voltage":
        return "Voltage"
      case "current":
        return "Current"
      case "co2":
        return "CO₂"
      case "pm25":
        return "PM2.5"
      case "pm10":
        return "PM10"
      case "voc":
        return "VOC"
      case "noise":
        return "Noise"
      case "motion":
        return "Motion"
      case "occupancy":
        return "Occupancy"
      case "water":
        return "Water"
      default:
        return type.charAt(0).toUpperCase() + type.slice(1)
    }
  }

  const getPrecision = () => {
    switch (type) {
      case "temperature":
      case "humidity":
        return 1
      case "voltage":
        return 1
      case "current":
      case "power":
        return 2
      default:
        return 0
    }
  }

  const hasIssues = criticalCount > 0 || warningCount > 0

  return (
    <Card className={hasIssues ? "border-warning" : ""}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-1">
            {getTypeIcon()}
            {getTypeName()}
          </span>
          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{count} sensors</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center mb-2">
          <div className="text-2xl font-bold">
            {avgValue.toFixed(getPrecision())} {unit}
          </div>
          <div className="text-xs text-muted-foreground">Average Value</div>
        </div>

        {hasIssues && (
          <div className="flex gap-2 mt-3">
            {criticalCount > 0 && (
              <div className="flex-1 text-center p-1 bg-destructive/10 rounded">
                <span className="text-xs text-destructive font-medium">{criticalCount} Critical</span>
              </div>
            )}
            {warningCount > 0 && (
              <div className="flex-1 text-center p-1 bg-warning/10 rounded">
                <span className="text-xs text-warning font-medium">{warningCount} Warning</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
